import { type ReactElement } from 'react';
import { Select, selectStyle, type OptionBase, type SelectProps } from '@ff-it/ui';
import type { GroupBase, StylesConfig } from 'react-select';

const base: StylesConfig<any, boolean> = {
  ...selectStyle,
  control: (b, s) => ({ ...(selectStyle.control ? selectStyle.control(b, s) : b), margin: '8px 8px 6px' }),
  menu: (b, s) => ({
    ...(selectStyle.menu ? selectStyle.menu(b, s) : b),
    position: 'relative',
    boxShadow: 'none',
    margin: 0,
  }),
  menuList: (b, s) => ({ ...(selectStyle.menuList ? selectStyle.menuList(b, s) : b), paddingLeft: 0, paddingRight: 0 }),
};

const withHiddenControl: StylesConfig<any, boolean> = {
  ...base,
  control: () => ({
    display: 'flex',
    height: 0,
    minHeight: 0,
    padding: 0,
    margin: 0,
    border: 0,
    overflow: 'hidden',
    opacity: 0,
  }),
};

/**
 * A Select whose menu is always open and rendered inline rather than as its own floating dropdown — meant to be
 * embedded as the content of a surrounding popover / chip panel, so the panel itself is the menu (no react-select
 * popover stacked on top).
 */
export function EmbeddedSelect<
  Option extends OptionBase = OptionBase,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({ styles, isSearchable, ...props }: SelectProps<Option, IsMulti, Group>): ReactElement {
  const resolved = (styles ?? (isSearchable === false ? withHiddenControl : base)) as StylesConfig<
    Option,
    IsMulti,
    Group
  >;
  return (
    <Select<Option, IsMulti, Group>
      autoFocus
      menuIsOpen
      placeholder="Search…"
      noOptionsMessage={({ inputValue }) => (inputValue ? 'No matches' : 'Type to search')}
      components={{ DropdownIndicator: null, IndicatorSeparator: null }}
      {...props}
      isSearchable={isSearchable}
      styles={resolved}
    />
  );
}
